import { appServiceApi } from "../../app/appServiceApi";
import { API_URL } from "../../constants/apiUrl";
import {
  GetCommonResponse,
  GetQueryArgsType,
  PatchQueryArgsType,
} from "../../types";
import {
  GetAllGroupByUserIdParams,
  GetAllUserPermissionId,
  GetLogByUserId,
  GetLogByUserIdParams,
  TGetAllPermission,
  TGetAllPermissionParams,
  TGetAllProjectWiseModule,
  TGetPermissionDetails,
} from "./administrativeSetting.type";

export const administrativeSetting = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjectWiseModule: builder.query<
      GetCommonResponse<TGetAllProjectWiseModule>,
      GetQueryArgsType<{ projectId?: number }>
    >({
      query: (queryArgs) => ({
        url: API_URL.getProjectWiseModule,
        params: queryArgs.params,
      }),
    }),
    getAllPermission: builder.query<
      GetCommonResponse<TGetAllPermission>,
      GetQueryArgsType<TGetAllPermissionParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllPermission,
        params: queryArgs.params,
      }),
      providesTags: ["GetAllPermission"],
    }),
    getPermissionDetails: builder.query<
      GetCommonResponse<TGetPermissionDetails>,
      number
    >({
      query: (id) => ({
        url: `${API_URL.getPermissionDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetPermissionDetails", id }],
    }),
    createPermission: builder.mutation({
      query: (data) => ({
        url: API_URL.createPermission,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllPermission"];
        return [];
      },
    }),
    updatePermission: builder.mutation<void, PatchQueryArgsType>({
      query: ({ id, body }) => ({
        url: `${API_URL.updatePermission}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetAllPermission",
            {
              type: "GetPermissionDetails",
              id,
            },
          ];

        return [];
      },
    }),
    updateBulkStatusPermission: builder.mutation({
      query: (data) => ({
        url: API_URL.updateBulkStatusPermission,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, _, body) => {
        if (!result) return [];

        const tags: any[] = ["GetAllPermission"];

        if (Array.isArray(body?.ids)) {
          body.ids.forEach((id: number) => {
            tags.push({ type: "GetPermissionDetails", id });
          });
        }

        return tags;
      },
    }),
    deletePermission: builder.mutation<void, number>({
      query: (id) => ({
        url: `${API_URL.deletePermission}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, _, id) => {
        if (result)
          return [
            "GetAllPermission",
            {
              type: "GetPermissionDetails",
              id,
            },
          ];

        return [];
      },
    }),
    groupPermissionAssign: builder.mutation({
      query: (data) => ({
        url: API_URL.groupPermissionAssign,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllUserPermissionId", "GetAllGroupByUserId"];
        return [];
      },
    }),
    groupPermissionUpdate: builder.mutation({
      query: (data) => ({
        url: API_URL.groupPermissionUpdate,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllUserPermissionId", "GetAllGroupByUserId"];
        return [];
      },
    }),
    groupPermissionRemove: builder.mutation({
      query: () => ({
        url: API_URL.groupPermissionRemove,
        method: "DELETE",
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllUserPermissionId", "GetAllGroupByUserId"];

        return [];
      },
    }),
    getAllUserPermissionId: builder.query<
      GetCommonResponse<GetAllUserPermissionId>,
      number
    >({
      query: (id) => ({
        url: `${API_URL.getAllUserPermissionId}/${id}`,
      }),
      providesTags: () => ["GetAllUserPermissionId"],
    }),
    getAllGroupByUserId: builder.query<
      GetCommonResponse<GetAllUserPermissionId>,
      GetQueryArgsType<GetAllGroupByUserIdParams>
    >({
      query: ({ params }) => {
        const { id, ...rest } = params as GetAllGroupByUserIdParams;

        return {
          url: `${API_URL.getAllGroupByUserId}/${id}`,
          params: { ...rest },
        };
      },
      providesTags: () => ["GetAllGroupByUserId"],
    }),
    getLogByUserId: builder.query<
      GetCommonResponse<GetLogByUserId>,
      GetQueryArgsType<GetLogByUserIdParams>
    >({
      query: ({ params }) => {
        const { id, ...rest } = params as GetLogByUserIdParams;

        return {
          url: `${API_URL.getLogByUserId}/${id}`,
          params: { ...rest },
        };
      },
      providesTags: () => ["GetLogByUserId"],
    }),
  }),
});

export const {
  useGetProjectWiseModuleQuery,
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
  useUpdateBulkStatusPermissionMutation,
  useGetAllPermissionQuery,
  useGetPermissionDetailsQuery,
  useDeletePermissionMutation,
  useGetAllGroupByUserIdQuery,
  useGetAllUserPermissionIdQuery,
  useGroupPermissionAssignMutation,
  useGroupPermissionUpdateMutation,
  useGroupPermissionRemoveMutation,
  useGetLogByUserIdQuery,
} = administrativeSetting;
